import React, { useState, useEffect } from 'react';
import {
  getCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon
} from '../../Services/Operations/ProductAPI';
import '../../Styles/ManagementPages.css';
import { useSelector } from 'react-redux';

const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>Coupon Code</th>
          <th>Coupon Type</th>
          <th>Discount Type</th>
          <th>Value</th>
          <th>Used/Limit</th>
          <th>Valid Until</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const SkeletonModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <div key={i} className="skeleton-detail-row">
            <div className="skeleton-label"></div>
            <div className="skeleton-value"></div>
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const SkeletonEditModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 edit-modal skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div key={i} className="form-group">
            <div className="skeleton-label"></div>
            <div className="skeleton-input"></div>
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const CouponManage = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [viewingCoupon, setViewingCoupon] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    type: 'Percentage',
    value: '',
    minAmount: '',
    maxDiscount: '',
    limit: '',
    validUntil: '',
    description: '',
    status: 'Active',
    couponType: 'normal'
  });
  const [error, setError] = useState(null);
  const { token } = useSelector(state => state.user);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCoupons, setTotalCoupons] = useState(0);
  const [pageSize] = useState(10);

  const [filters, setFilters] = useState({
    status: 'All Status',
    type: 'All Types',
    couponType: 'All Coupon Types',
    code: '',
    description: '',
    minValue: '',
    maxValue: '',
    minUsage: '',
    maxUsage: '',
    validFrom: '',
    validTo: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchCoupons();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, searchTerm, filters]);

  const formatDate = (dateString) => {
  if (!dateString || dateString === 'N/A') return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

  const fetchCoupons = async () => {
    try {
      setSearching(true);
      setLoading(true);
      setError(null);

      const filterParams = {};

      if (searchTerm) {
        filterParams.search = searchTerm;
      }

      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key] !== 'All Status' && filters[key] !== 'All Types' && filters[key] !== 'All Coupon Types') {
          filterParams[key] = filters[key];
        }
      });

      const params = {
        page: currentPage,
        limit: pageSize,
        ...filterParams
      };

      console.log('Fetching coupons with params:', params);

      const response = await getCoupons(token, params);

      if (response && response.success) {
        const transformedCoupons = response.data.map(coupon => ({
          id: coupon._id,
          code: coupon.code,
          couponType: coupon.couponType,
          type: coupon.type,
          value: coupon.value,
          discountValue: coupon.type === 'Percentage' ? `${coupon.value}%` : `₹${coupon.value}`,
          minAmount: coupon.minAmount,
          maxDiscount: coupon.maxDiscount,
          used: coupon.used || 0,
          limit: coupon.limit,
          validUntil: coupon.validUntil ? coupon.validUntil.split('T')[0] : 'N/A',
          status: coupon.status,
          description: coupon.description,
          created: coupon.createdAt ? coupon.createdAt.split('T')[0] : 'N/A',
          commissionRate: coupon.commissionRate || null,
          influencerId: coupon.influencerId || null,
          rawData: coupon
        }));

        setCoupons(transformedCoupons);

        if (response.totalPages) {
          setTotalPages(response.totalPages);
        }
        if (response.total) {
          setTotalCoupons(response.total);
        } else {
          setTotalCoupons(response.data.length);
        }

        const hasActiveFilters =
          filters.status !== 'All Status' ||
          filters.type !== 'All Types' ||
          filters.couponType !== 'All Coupon Types' ||
          searchTerm !== '' ||
          Object.values(filters).some(value =>
            value && value !== '' && value !== 'All Status' && value !== 'All Types' && value !== 'All Coupon Types'
          );
        setIsFiltered(hasActiveFilters);
      } else {
        setError('Failed to fetch coupons.');
        setCoupons([]);
      }
    } catch (err) {
      console.error('Error fetching coupons:', err);
      setError('Failed to fetch coupons. Please try again.');
      setCoupons([]);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const getSafeString = (value) => {
    if (value === null || value === undefined) return '';
    return String(value);
  };

  const getSafeLowerCase = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).toLowerCase();
  };

  const getSafeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    return Number(value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchCoupons();
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchCoupons();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      status: 'All Status',
      type: 'All Types',
      couponType: 'All Coupon Types',
      code: '',
      description: '',
      minValue: '',
      maxValue: '',
      minUsage: '',
      maxUsage: '',
      validFrom: '',
      validTo: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
    setIsFiltered(false);
  };

  const handleExportCSV = () => {
    if (coupons.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['Coupon Code', 'Coupon Type', 'Discount Type', 'Value', 'Min Amount', 'Max Discount', 'Used', 'Limit', 'Valid Until', 'Status', 'Description'];
    const csvData = coupons.map(coupon => [
      coupon.code || '',
      coupon.couponType || '',
      coupon.type || '',
      coupon.discountValue || '',
      coupon.minAmount || 0,
      coupon.maxDiscount || 0,
      coupon.used || 0,
      coupon.limit || 0,
      coupon.validUntil || '',
      coupon.status || '',
      coupon.description || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `coupons-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    alert('CSV exported successfully!');
  };

  const handleViewCoupon = (coupon) => {
    setViewingCoupon(coupon);
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon.id);
    setEditForm({
      ...coupon,
      value: coupon.value,
      minAmount: coupon.minAmount,
      maxDiscount: coupon.maxDiscount,
      limit: coupon.limit
    });
  };

  const handleSaveEdit = async () => {
    try {
      setError(null);

      const { id, discountValue, created, commissionRate, influencerId, rawData, ...apiData } = editForm;

      const response = await updateCoupon(editingCoupon, apiData, token);

      if (response.success) {
        setCoupons(prevCoupons =>
          prevCoupons.map(coupon =>
            coupon.id === editingCoupon ? {
              ...editForm,
              discountValue: editForm.type === 'Percentage' ? `${editForm.value}%` : `₹${editForm.value}`
            } : coupon
          )
        );
        setEditingCoupon(null);
        setEditForm({});
        alert('Coupon updated successfully!');
      } else {
        throw new Error(response.message || 'Failed to update coupon');
      }
    } catch (err) {
      console.error('Error updating coupon:', err);
      setError('Failed to update coupon. Please try again.');
      alert('Failed to update coupon. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingCoupon(null);
    setEditForm({});
    setError(null);
  };

  const handleDeleteCoupon = async (couponId) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        setError(null);
        const response = await deleteCoupon(couponId, token);

        if (response.success) {
          setCoupons(prevCoupons => prevCoupons.filter(coupon => coupon.id !== couponId));
          setTotalCoupons(prev => prev - 1);
          alert('Coupon deleted successfully!');
        } else {
          throw new Error(response.message || 'Failed to delete coupon');
        }
      } catch (err) {
        console.error('Error deleting coupon:', err);
        setError('Failed to delete coupon. Please try again.');
        alert('Failed to delete coupon. Please try again.');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: name === 'value' || name === 'minAmount' || name === 'maxDiscount' || name === 'limit' ? parseInt(value) || '' : value
    }));
  };

  const handleNewCouponChange = (e) => {
    const { name, value } = e.target;
    setNewCoupon(prev => ({
      ...prev,
      [name]: name === 'value' || name === 'minAmount' || name === 'maxDiscount' || name === 'limit' ? parseInt(value) || '' : value
    }));
  };

const handleCreateCoupon = async () => {
  try {
    setError(null);

    let apiData = {
      code: newCoupon.code,
      description: newCoupon.description,
      minAmount: parseInt(newCoupon.minAmount) || 0,
      validUntil: newCoupon.validUntil,
      status: newCoupon.status,
      couponType: newCoupon.couponType
    };

    if (newCoupon.couponType === 'influencer') {
      apiData.commissionRate = parseInt(newCoupon.commissionRate) || 0;
    } else {
      apiData.type = newCoupon.type;
      apiData.value = parseInt(newCoupon.value) || 0;
      apiData.maxDiscount = parseInt(newCoupon.maxDiscount) || 0;
      apiData.limit = parseInt(newCoupon.limit) || 0;
    }

    const response = await createCoupon(apiData, token);

      if (response.success) {
        fetchCoupons();
        setShowCreateModal(false);
        setNewCoupon({
          code: '',
          type: 'Percentage',
          value: '',
          minAmount: '',
          maxDiscount: '',
          limit: '',
          validUntil: '',
          description: '',
          status: 'Active',
          couponType: 'normal'
        });
        alert('Coupon created successfully!');
      } else {
        throw new Error(response.message || 'Failed to create coupon');
      }
    } catch (err) {
      console.error('Error creating coupon:', err);
      setError('Failed to create coupon. Please try again.');
      alert('Failed to create coupon. Please try again.');
    }
  };

  const closeViewModal = () => {
    setViewingCoupon(null);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewCoupon({
      code: '',
      type: 'Percentage',
      value: '',
      minAmount: '',
      maxDiscount: '',
      limit: '',
      validUntil: '',
      description: '',
      status: 'Active',
      couponType: 'normal'
    });
    setError(null);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
    clearFilters();
  };

  const activeCoupons = coupons.filter(c => getSafeLowerCase(c.status) === 'active').length;
  const normalCoupons = coupons.filter(c => getSafeLowerCase(c.couponType) === 'normal').length;
  const influencerCoupons = coupons.filter(c => getSafeLowerCase(c.couponType) === 'influencer').length;
  const totalUsage = coupons.reduce((sum, coupon) => sum + getSafeNumber(coupon.used), 0);

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, start + maxVisiblePages - 1);

      if (end - start + 1 < maxVisiblePages) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  if (loading && coupons.length === 0) {
    return (
      <div className="management-page">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>

        <div className="stats-cards">
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>

        <SkeletonTable />

        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Coupon Management</h1>
        <div className="header-actions">
          <form onSubmit={handleSearchSubmit} className="search-form">
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search by coupon code..."
                className="search-input"
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ width: "70%" }}
              />
              {searching && (
                <span style={{
                  position: 'absolute', right: '10px', top: '50%',
                  transform: 'translateY(-50%)', fontSize: '12px', color: '#888'
                }}>
                  searching...
                </span>
              )}
            </div>
          </form>

          <button className="refresh-btn" onClick={handleRefresh}>
            Refresh
          </button>
          <button className="export-btn" onClick={handleExportCSV}>
            Export CSV
          </button>
          <button className="add-btn" onClick={() => setShowCreateModal(true)}>
            + Create Coupon
          </button>
        </div>
      </div>


      {showFilters && (
        <div className="filter-panel">
          <div className="filter-header">
            <h3>Filter Coupons</h3>
            <button className="close-filters" onClick={() => setShowFilters(false)}>
              ×
            </button>
          </div>
          <div className="filter-grid">
            <div className="filter-group">
              <label>Coupon Code</label>
              <input
                type="text"
                name="code"
                value={filters.code}
                onChange={handleFilterChange}
                placeholder="Enter coupon code"
              />
            </div>
            <div className="filter-group">
              <label>Description</label>
              <input
                type="text"
                name="description"
                value={filters.description}
                onChange={handleFilterChange}
                placeholder="Enter description"
              />
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="All Status">All Status</option>
                <option value="Active">Active</option>
                <option value="Expired">Expired</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Coupon Type</label>
              <select
                name="couponType"
                value={filters.couponType}
                onChange={handleFilterChange}
              >
                <option value="All Coupon Types">All Types</option>
                <option value="normal">Normal</option>
                <option value="influencer">Influencer</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Discount Type</label>
              <select
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
              >
                <option value="All Types">All Types</option>
                <option value="Percentage">Percentage</option>
                <option value="Fixed">Fixed</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Min Discount Value</label>
              <input
                type="number"
                name="minValue"
                value={filters.minValue}
                onChange={handleFilterChange}
                placeholder="Minimum value"
                min="0"
              />
            </div>
            <div className="filter-group">
              <label>Max Discount Value</label>
              <input
                type="number"
                name="maxValue"
                value={filters.maxValue}
                onChange={handleFilterChange}
                placeholder="Maximum value"
                min="0"
              />
            </div>
            <div className="filter-group">
              <label>Min Usage</label>
              <input
                type="number"
                name="minUsage"
                value={filters.minUsage}
                onChange={handleFilterChange}
                placeholder="Minimum usage"
                min="0"
              />
            </div>
            <div className="filter-group">
              <label>Max Usage</label>
              <input
                type="number"
                name="maxUsage"
                value={filters.maxUsage}
                onChange={handleFilterChange}
                placeholder="Maximum usage"
                min="0"
              />
            </div>
            <div className="filter-group">
              <label>Valid From</label>
              <input
                type="date"
                name="validFrom"
                value={filters.validFrom}
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label>Valid To</label>
              <input
                type="date"
                name="validTo"
                value={filters.validTo}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="apply-filters-btn" onClick={applyFilters}>
              Apply Filters
            </button>
            <button className="clear-filters-btn" onClick={clearFilters}>
              Clear All
            </button>
          </div>
          {isFiltered && (
            <div className="active-filters">
              <span>Active Filters:</span>
              {searchTerm && (
                <span className="filter-tag">Search: {searchTerm}</span>
              )}
              {filters.status !== 'All Status' && (
                <span className="filter-tag">Status: {filters.status}</span>
              )}
              {filters.type !== 'All Types' && (
                <span className="filter-tag">Discount Type: {filters.type}</span>
              )}
              {filters.couponType !== 'All Coupon Types' && (
                <span className="filter-tag">Coupon Type: {filters.couponType}</span>
              )}
              {Object.entries(filters).map(([key, value]) =>
                value &&
                key !== 'status' &&
                key !== 'type' &&
                key !== 'couponType' &&
                key !== 'validFrom' &&
                key !== 'validTo' && (
                  <span key={key} className="filter-tag">
                    {key}: {value}
                  </span>
                )
              )}
              {filters.validFrom && (
                <span className="filter-tag">Valid From: {filters.validFrom}</span>
              )}
              {filters.validTo && (
                <span className="filter-tag">Valid To: {filters.validTo}</span>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total Coupons</h3>
          <p>{totalCoupons}</p>
          <span>All coupons</span>
        </div>
        <div className="stat-card">
          <h3>Active Coupons</h3>
          <p>{activeCoupons}</p>
          <span>Active coupons</span>
        </div>
        <div className="stat-card">
          <h3>Normal Coupons</h3>
          <p>{normalCoupons}</p>
          <span>Normal coupons</span>
        </div>
        <div className="stat-card">
          <h3>Influencer Coupons</h3>
          <p>{influencerCoupons}</p>
          <span>Influencer coupons</span>
        </div>
        <div className="stat-card">
          <h3>Total Usage</h3>
          <p>{totalUsage}</p>
          <span>Total coupon uses</span>
        </div>
      </div>


      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Coupon Code</th>
              <th>Coupon Type</th>
              <th>Discount Type</th>
              <th>Value</th>
              <th>Used/Limit</th>
              <th>Valid Until</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {coupons.length > 0 ? (
              coupons.map(coupon => (
                <tr key={coupon.id}>
                  <td className="coupon-code">{getSafeString(coupon.code)}</td>

                  <td>
                    <span className={`coupon-type-badge ${getSafeLowerCase(coupon.couponType)}`}>
                      {coupon.couponType ? coupon.couponType.charAt(0).toUpperCase() + coupon.couponType.slice(1) : 'Normal'}
                    </span>
                  </td>
                  <td>
                    {editingCoupon === coupon.id ? (
                      <select
                        name="type"
                        value={editForm.type || ''}
                        onChange={handleInputChange}
                        className="edit-select"
                      >
                        <option value="Percentage">Percentage</option>
                        <option value="Fixed">Fixed</option>
                      </select>
                    ) : (
                      getSafeString(coupon.type)
                    )}
                  </td>
                  <td>
                    {editingCoupon === coupon.id ? (
                      <input
                        type="number"
                        name="value"
                        value={editForm.value || ''}
                        onChange={handleInputChange}
                        className="edit-input"
                        placeholder={editForm.type === 'Percentage' ? 'Percentage' : 'Amount'}
                      />
                    ) : (
                      getSafeString(coupon.discountValue)
                    )}
                  </td>
                  <td>
                    <div className="usage-info">
                      <span className="used-count">{getSafeNumber(coupon.used)}</span>
                      <span className="usage-separator">/</span>
                      <span className="limit-count">{getSafeNumber(coupon.limit)}</span>
                    </div>
                    {editingCoupon === coupon.id && (
                      <input
                        type="number"
                        name="limit"
                        value={editForm.limit || ''}
                        onChange={handleInputChange}
                        className="edit-input small"
                        placeholder="Limit"
                      />
                    )}
                  </td>
                  <td>
                    {editingCoupon === coupon.id ? (
                      <input
                        type="date"
                        name="validUntil"
                        value={editForm.validUntil || ''}
                        onChange={handleInputChange}
                        className="edit-input"
                      />
                    ) : (
                      formatDate(coupon.validUntil)
                    )}
                  </td>
                  <td>
                    {editingCoupon === coupon.id ? (
                      <select
                        name="status"
                        value={editForm.status || ''}
                        onChange={handleInputChange}
                        className="edit-select"
                      >
                        <option value="Active">Active</option>
                        <option value="Expired">Expired</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    ) : (
                      <span className={`status-badge ${getSafeLowerCase(coupon.status)}`}>
                        {getSafeString(coupon.status)}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingCoupon === coupon.id ? (
                      <div className="edit-actions">
                        <button className="action-btn save" onClick={handleSaveEdit}>
                          Save
                        </button>
                        <button className="action-btn cancel" onClick={handleCancelEdit}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="action-buttons">
                        <button
                          className="action-btn view"
                          onClick={() => handleViewCoupon(coupon)}
                        >
                          View
                        </button>
                        <button
                          className="action-btn edit"
                          onClick={() => handleEditCoupon(coupon)}
                        >
                          Edit
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => handleDeleteCoupon(coupon.id)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">
                  <p>No coupons found {isFiltered ? 'matching your filters' : ''}.</p>
                  {isFiltered && (
                    <button className="clear-filters-link" onClick={clearFilters}>
                      Clear filters to see all coupons
                    </button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      <div className="pagination-container">
        <div className="pagination-info">
          Showing {coupons.length} of {totalCoupons} coupons
          (Page {currentPage} of {totalPages})
          {searchTerm && <span className="filtered-indicator"> • Search: "{searchTerm}"</span>}
          {isFiltered && !searchTerm && <span className="filtered-indicator"> • Filtered Results</span>}
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>


          <div className="page-numbers">
            {generatePageNumbers().map(pageNumber => (
              <button
                key={pageNumber}
                className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                onClick={() => handlePageClick(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}

            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                <span className="page-dots">...</span>
                <button
                  className="page-number"
                  onClick={() => handlePageClick(totalPages)}
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>

          <button
            className="pagination-btn"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>


      {viewingCoupon && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Coupon Details - {getSafeString(viewingCoupon.code)}</h3>
              <button className="modal-close-btn" onClick={closeViewModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <div className="detail-row">
                  <label>Coupon Code:</label>
                  <span className="coupon-code">{getSafeString(viewingCoupon.code)}</span>
                </div>
                <div className="detail-row">
                  <label>Coupon Type:</label>
                  <span className={`coupon-type-badge ${getSafeLowerCase(viewingCoupon.couponType)}`}>
                    {getSafeString(viewingCoupon.couponType)}
                  </span>
                  {viewingCoupon.couponType === 'influencer' && viewingCoupon.commissionRate && (
                    <span className="commission-info">({viewingCoupon.commissionRate}% commission)</span>
                  )}
                </div>
                <div className="detail-row">
                  <label>Description:</label>
                  <span>{getSafeString(viewingCoupon.description)}</span>
                </div>
                <div className="detail-row">
                  <label>Discount Type:</label>
                  <span>{getSafeString(viewingCoupon.type)}</span>
                </div>
                <div className="detail-row">
                  <label>Discount Value:</label>
                  <span>{getSafeString(viewingCoupon.discountValue)}</span>
                </div>
                <div className="detail-row">
                  <label>Minimum Amount:</label>
                  <span>₹{getSafeNumber(viewingCoupon.minAmount)}</span>
                </div>
                <div className="detail-row">
                  <label>Maximum Discount:</label>
                  <span>₹{getSafeNumber(viewingCoupon.maxDiscount)}</span>
                </div>
                <div className="detail-row">
                  <label>Usage:</label>
                  <span>{getSafeNumber(viewingCoupon.used)} / {getSafeNumber(viewingCoupon.limit)}</span>
                </div>
                <div className="detail-row">
                  <label>Remaining Uses:</label>
                  <span>{getSafeNumber(viewingCoupon.limit - viewingCoupon.used)}</span>
                </div>
                <div className="detail-row">
                  <label>Valid Until:</label>
                  <span>{formatDate(viewingCoupon.validUntil)}</span>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`status-badge ${getSafeLowerCase(viewingCoupon.status)}`}>
                    {getSafeString(viewingCoupon.status)}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Created On:</label>
                  <span>{formatDate(viewingCoupon.created)}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {editingCoupon && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content1 edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Coupon - {getSafeString(editForm.code)}</h3>
              <button className="modal-close-btn" onClick={handleCancelEdit}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="edit-form">
                <div className="form-group">
                  <label>Coupon Code:</label>
                  <input
                    type="text"
                    name="code"
                    value={editForm.code || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Coupon Code"
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label>Description:</label>
                  <input
                    type="text"
                    name="description"
                    value={editForm.description || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Description"
                  />
                </div>
                <div className="form-group">
                  <label>Discount Type:</label>
                  <select
                    name="type"
                    value={editForm.type || 'Percentage'}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    <option value="Percentage">Percentage</option>
                    <option value="Fixed">Fixed Amount</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Discount Value:</label>
                  <input
                    type="number"
                    name="value"
                    value={editForm.value || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder={editForm.type === 'Percentage' ? 'Percentage' : 'Amount'}
                  />
                </div>
                
                <div className="form-group">
                  <label>Minimum Amount:</label>
                  <input
                    type="number"
                    name="minAmount"
                    value={editForm.minAmount || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Minimum order amount"
                  />
                </div>
                 {editForm.couponType !== 'influencer' && (
                  <>
                <div className="form-group">
                  <label>Maximum Discount:</label>
                  <input
                    type="number"
                    name="maxDiscount"
                    value={editForm.maxDiscount || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Maximum discount amount"
                  />
                </div>
               
                <div className="form-group">
                  <label>Usage Limit:</label>
                  <input
                    type="number"
                    name="limit"
                    value={editForm.limit || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Total usage limit"
                  />
                </div>
                 </>
                 )}
                <div className="form-group">
                  <label>Valid Until:</label>
                  <input
  type="date"
  name="validUntil"
  value={editForm.validUntil?.split('T')[0] || ''}
  onChange={handleInputChange}
  className="form-control"
/>
                </div>
                <div className="form-group">
                  <label>Status:</label>
                  <select
                    name="status"
                    value={editForm.status || 'Active'}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    <option value="Active">Active</option>
                    <option value="Expired">Expired</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={handleCancelEdit}>
                Cancel
              </button>
              <button className="action-btn save" onClick={handleSaveEdit}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}


      {showCreateModal && (
        <div className="modal-overlay" onClick={closeCreateModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Coupon</h3>
              <button className="modal-close-btn" onClick={closeCreateModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <div className="detail-row">
                  <label>Coupon Type:</label>
                  <select
                    name="couponType"
                    value={newCoupon.couponType}
                    onChange={handleNewCouponChange}
                    className="edit-select"
                  >
                    <option value="normal">Normal Coupon</option>
                    <option value="influencer">Influencer Coupon</option>
                  </select>
                </div>
                <div className="detail-row">
                  <label>Coupon Code:</label>
                  <input
                    type="text"
                    name="code"
                    value={newCoupon.code}
                    onChange={handleNewCouponChange}
                    className="edit-input"
                    placeholder="Enter coupon code"
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                {newCoupon.couponType === 'influencer' && (
                  <div className="detail-row">
                    <label>Commission Rate (%):</label>
                    <input
                      type="number"
                      name="commissionRate"
                      value={newCoupon.commissionRate || ''}
                      onChange={handleNewCouponChange}
                      className="edit-input"
                      placeholder="Commission percentage"
                      min="0"
                      max="100"
                    />
                  </div>
                )}
                <div className="detail-row">
                  <label>Description:</label>
                  <input
                    type="text"
                    name="description"
                    value={newCoupon.description}
                    onChange={handleNewCouponChange}
                    className="edit-input"
                    placeholder="Enter coupon description"
                  />
                </div>
                {newCoupon.couponType === 'normal' && (
                  <>
                    <div className="detail-row">
                      <label>Discount Type:</label>
                      <select
                        name="type"
                        value={newCoupon.type}
                        onChange={handleNewCouponChange}
                        className="edit-select"
                      >
                        <option value="Percentage">Percentage</option>
                        <option value="Fixed">Fixed Amount</option>
                      </select>
                    </div>
                    <div className="detail-row">
                      <label>Discount Value:</label>
                      <input
                        type="number"
                        name="value"
                        value={newCoupon.value}
                        onChange={handleNewCouponChange}
                        className="edit-input"
                        placeholder={newCoupon.type === 'Percentage' ? 'Percentage' : 'Amount'}
                      />
                    </div>
                  </>
                )}
                <div className="detail-row">
                  <label>Minimum Amount:</label>
                  <input
                    type="number"
                    name="minAmount"
                    value={newCoupon.minAmount}
                    onChange={handleNewCouponChange}
                    className="edit-input"
                    placeholder="Minimum order amount"
                  />
                </div>


                {newCoupon.couponType !== 'influencer' && (
                  <>
                    <div className="detail-row">
                      <label>Maximum Discount:</label>
                      <input
                        type="number"
                        name="maxDiscount"
                        value={newCoupon.maxDiscount}
                        onChange={handleNewCouponChange}
                        className="edit-input"
                        placeholder="Maximum discount amount"
                      />
                    </div>
                    <div className="detail-row">
                      <label>Usage Limit:</label>
                      <input
                        type="number"
                        name="limit"
                        value={newCoupon.limit}
                        onChange={handleNewCouponChange}
                        className="edit-input"
                        placeholder="Total usage limit"
                      />
                    </div>
                  </>
                )}
                <div className="detail-row">
                  <label>Valid Until:</label>
                  <input
                    type="date"
                    name="validUntil"
                    value={newCoupon.validUntil}
                    onChange={handleNewCouponChange}
                    className="edit-input"
                  />
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <select
                    name="status"
                    value={newCoupon.status}
                    onChange={handleNewCouponChange}
                    className="edit-select"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={closeCreateModal}>
                Cancel
              </button>
              <button className="action-btn save" onClick={handleCreateCoupon}>
                Create Coupon
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponManage;